import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { usePermissions } from "@/features/auth/hooks/usePermissions";
import {
  normalizedPreflightTenantId,
  preflightBrandsQueryOptions,
  preflightKeys,
} from "@/lib/queries";
import { createBrand, deleteBrand, renameBrand } from "../api/preflight.api";
import type { CreatePreflightBrandBody, PreflightBrand } from "../types/preflight.types";

const NO_BRANDS: PreflightBrand[] = [];

/** The tenant's brand names, for the New report form and the manage dialog. */
export function usePreflightBrands() {
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const query = useQuery(preflightBrandsQueryOptions(activeTenantId));
  return { ...query, brands: query.data ?? NO_BRANDS };
}

/**
 * What the Manage brands dialog can offer this caller. Rename and remove need a
 * brand to act on, so without the add key an empty list leaves nothing to manage.
 */
export function useBrandManagement(brands: PreflightBrand[]) {
  const permissions = usePermissions();
  const canAdd = permissions.PREFLIGHT_CREATE_BRAND.isOn;
  const canChangeExisting =
    permissions.PREFLIGHT_UPDATE_BRAND.isOn || permissions.PREFLIGHT_DELETE_BRAND.isOn;
  return { canAdd, canOpenManage: canAdd || (canChangeExisting && brands.length > 0) };
}

function useBrandListInvalidation() {
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const queryClient = useQueryClient();

  return () =>
    queryClient.invalidateQueries({
      queryKey: preflightKeys.brands(normalizedPreflightTenantId(activeTenantId)),
    });
}

/** Add a brand. The duplicate conflict surfaces in the caller's dialog, not a toast. */
export function useCreateBrand() {
  const invalidateBrands = useBrandListInvalidation();

  return useMutation<PreflightBrand, Error, CreatePreflightBrandBody>({
    mutationFn: (input) => createBrand(input),
    onSuccess: invalidateBrands,
    meta: { skipGlobalError: true },
  });
}

/** Rename a brand. A collision with another brand surfaces in the dialog, not a toast. */
export function useRenameBrand() {
  const invalidateBrands = useBrandListInvalidation();

  return useMutation<PreflightBrand, Error, { id: string; name: string }>({
    mutationFn: ({ id, name }) => renameBrand(id, { name }),
    onSuccess: invalidateBrands,
    meta: { skipGlobalError: true },
  });
}

/** Remove a brand. Existing reports keep their brand, so there is nothing to cascade. */
export function useDeleteBrand() {
  const invalidateBrands = useBrandListInvalidation();

  return useMutation<void, Error, string>({
    mutationFn: (id) => deleteBrand(id),
    onSuccess: invalidateBrands,
    meta: { skipGlobalError: true },
  });
}
